<script setup>
/* Imports */
import { ref, computed } from 'vue';
import { EmsModelPicker, useProductOptions } from '@garmin/ui-product-ems-filters';

/* Analytics */
import {
  sendAnalyticsEvent,
  sanitizeAnalyticsString,
  getCategory3Data,
  getAnalyticsPriceData,
} from '../helpers/ga/analytics.js';

/* Helpers */
import {
  mapGetters,
  mapActions,
  buildProductPageUrl,
  announceAccessibilityMessage,
} from '../helpers/mainHelpers.js';

/* Composables */
import { useGenericAnalyticsDataHelper } from '../composables/useAnalyticsData.js';
import { useHandleAffirmUpdate } from '../composables/useHandleAffirmUpdate.js';

/* Composables */
const productOptions = useProductOptions();
const { genericAnalyticsData } = useGenericAnalyticsDataHelper();
const { handleAffirmUpdate } = useHandleAffirmUpdate();

const preloadedSkus = ref([]);

/* Store - Getters */
const {
  getGlobalProductName,
  getLocale,
  getProductBySku,
  getPartNumber,
} = mapGetters();

const {
  updateProductData,
} = mapActions();

const modelOptionsList = computed(() => productOptions.getOptionListByType('image'));
const modelText = computed(() => {
  const productDetails = getProductBySku.value(getPartNumber.value);
  return productDetails?.productVariation ?? '';
});
const getUrl = (item) => buildProductPageUrl(item, {
  locale: getLocale.value ?? '',
  origin: window?.location?.origin,
});
const getImageAltText = (item) => item?.altText ?? null;

const getAnalyticsProductName = () => sanitizeAnalyticsString(getGlobalProductName.value) || 'NA';
const getCategory3Value = () => {
  const productName = getAnalyticsProductName();
  return getCategory3Data(productName);
};
const sendModelPickerAnalyticsEvent = (item) => {
  if (!item) return;

  const selectedProduct = getProductBySku.value(item?.sku);

  const productPrice = {
    priceObj: {
      listPrice: selectedProduct?.price?.listPrice,
      salePrice: selectedProduct?.price?.salePrice,
    },
    sellable: selectedProduct?.isSellable,
    showPrice: selectedProduct?.showPrice,
  };

  const selectedProductName = sanitizeAnalyticsString(selectedProduct?.globalProductName);
  const selectedProductIndex = productOptions.getOptionListByType('image')[0]?.options?.findIndex((option) => option.sku === item.sku);
  const productCategory4 = productOptions.getOptionListByType('ems')[0]?.options?.find((option) => option.sku === item.sku)?.text || 'NA';
  const productCategory5 = productOptions.getOptionListByType('image')[0]?.options?.find((option) => option.sku === item.sku)?.text || 'NA';

  const analyticsData = {
    ...genericAnalyticsData(),
    // Send the following fields as 'NA' for now since they are not available in the current context
    creative_name: 'NA',
    creative_slot: 'NA',
    location_id: 'NA',
    promotion_name: 'NA',
    promotion_id: 'NA',
    bundle_id: 'NA',
    is_bundle: 'NA',
    sale_type: 'NA',
    item_list_id: ['Product Page EMS'],
    item_list_name: [`${selectedProductName} - Product Page EMS - Thumbnail`],
    index: [selectedProductIndex + 1],
    product_id: selectedProduct?.productId ? [selectedProduct?.productId] : ['NA'],
    product_brand: window?.utag_data?.product_brand || ['NA'],
    product_category: window?.utag_data?.product_category || ['NA'],
    product_category2: window?.utag_data?.product_category2 || ['NA'],
    product_category3: getCategory3Value(),
    product_category4: [productCategory4],
    product_category5: [productCategory5],
    product_variant: selectedProduct?.productVariation ? [sanitizeAnalyticsString(selectedProduct?.productVariation)] : ['NA'],
    product_name: selectedProduct?.globalProductName ? [selectedProductName] : ['NA'],
    product_original_price: [getAnalyticsPriceData(productPrice).listPrice],
    product_price: [getAnalyticsPriceData(productPrice).price],
    product_sku: item?.sku ? [item.sku] : ['NA'],
    product_url: [getUrl(item) || 'NA'],
    product_availability: [getAnalyticsPriceData(productPrice).availability],
    item_family: getCategory3Value(),
    is_sale: getAnalyticsPriceData(productPrice).isSale,
    in_stock: getAnalyticsPriceData(productPrice).inStock,
    price_shown: getAnalyticsPriceData(productPrice).priceShown,
    link_type: ['EMS image'],
    tealium_event: 'select_item',
  };

  sendAnalyticsEvent(analyticsData);
};

const changeModel = async (item) => {
  const altText = getImageAltText(item);

  if (altText) {
    // Accessibility announcement
    announceAccessibilityMessage(altText);
  }

  // If we are already displaying this sku, don't update the product data
  if (item.sku === getPartNumber.value) {
    return;
  }

  // Send analytics event
  sendModelPickerAnalyticsEvent(item);

  // Retrieve the updated product data from Store
  const selectedProduct = getProductBySku.value(item.sku);

  // Update Affirm promotions with the new price
  await handleAffirmUpdate(selectedProduct);

  // Update the product options composable BEFORE updating the store so that
  // the `getPartNumber` watcher in ProductPage.vue (which fires during
  // `updateProductData` and dispatches page_view / view_item) reads the fresh
  // options for the newly selected SKU — otherwise the resulting analytics
  // fields such as `index` and `product_category4` are computed from the
  // previous SKU's options.
  productOptions.setFilterDropdownList(window?.emsData?.filterDropDown ?? []);
  productOptions.setProductOptions({
    optionsList: window?.emsData?.emsDetails ?? [],
    options: window?.emsData?.skuVariantMatrix?.[item?.sku] ?? {},
  });

  // Update the product data in the store and trigger a re-render of the product page with the new product data
  await updateProductData(item.sku);
};

const preloadImages = (item) => {
  // If we already preloaded it or is the current product do nothing
  if (preloadedSkus.value[item.sku] || getPartNumber.value === item.sku) return;

  const productDetails = getProductBySku.value(item.sku);

  // In case the SKU is not available simply return
  if (!productDetails) return;

  // We want the default image and the rest of the gallery
  const images = [{ ...productDetails.images.defaultImage }, ...(productDetails.images.gallery || [])];

  // Do the actual preloading
  images.forEach((imgObj) => {
    const imgSrc = imgObj.image || imgObj.imageSrc;
    if (!imgSrc) return;
    const img = new Image();
    img.src = imgSrc;
  });

  // Add the SKU to preloaded ones
  preloadedSkus.value[item.sku] = true;
};

const handleClick = (item) => {
  changeModel(item);
};

const handleMouseover = (item) => {
  preloadImages(item);
};
</script>

<template>
  <EmsModelPicker
    :model-options-list="modelOptionsList"
    :model-text="modelText"
    :format-url="getUrl"
    @ems-model-picker-click="handleClick($event)"
    @ems-model-picker-mouseover="handleMouseover($event)"
  />
</template>
